import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiPlus, FiSearch, FiChevronLeft, FiChevronRight, FiFileText, FiX,
  FiFile, FiType, FiGrid, FiList, FiCommand, FiArrowRight,
  FiCalendar, FiDollarSign, FiTrendingUp, FiClock, FiCheckCircle,
  FiLayers, FiBriefcase
} from 'react-icons/fi';
import { fetchContracts } from '../../store/slices/contractsSlice';
import { contractsAPI, companiesAPI } from '../../services/api';
import { differenceInDays } from 'date-fns';
import { formatDateMedium } from '../../utils/dateFormat';
import { formatMoney } from '../../utils/numberFormat';
import { contractStatusLabel } from '../../utils/contractStatus';
import ContractsTable from './ContractsTable';
import SearchableSelect from '../../components/SearchableSelect';
import PageHeader from '../../components/PageHeader/PageHeader';
import StickyHeaderAction from '../../components/StickyHeaderAction';
import './ContractList.scss';

// Hit type → { label, icon } metadata for the live search dropdown
const HIT_TYPE_META = {
  contract:         { label: 'Contract',         icon: FiFileText },
  field:            { label: 'Field',            icon: FiType },
  document:         { label: 'Document',         icon: FiFile },
  document_content: { label: 'Document content', icon: FiFile },
  attachment:       { label: 'Attachment',       icon: FiFile },
};

const TYPE_OPTIONS = [
  'nda', 'msa', 'sow', 'employment', 'vendor_agreement', 'lease_agreement'
];

// Status groups behind the Aktif / Bekleyen filters — must stay in sync with
// what GET /contracts/stats counts, or a card's number and the list it opens
// will disagree (e.g. 19 active, empty list).
const ACTIVE_STATUSES = 'active,fully_executed';
const PENDING_STATUSES = 'pending_approval,in_review';

// Quick-filter segmented tabs (value '' = All). Drafts are personal and live
// in My Tasks → Drafts, so they are not listed here.
const STATUS_TABS = [
  { value: '',               icon: FiLayers,      key: 'all' },
  { value: ACTIVE_STATUSES,  icon: FiCheckCircle, key: 'active' },
  { value: 'expiring_soon',  icon: FiClock,       key: 'expiring' },
  { value: PENDING_STATUSES, icon: FiTrendingUp,  key: 'pending' },
  { value: 'archived',       icon: FiFile,        key: 'archived' },
];

const SORT_OPTIONS = [
  { value: 'recent',    key: 'recent' },
  { value: 'oldest',    key: 'oldest' },
  { value: 'az',        key: 'az' },
  { value: 'za',        key: 'za' },
  { value: 'value_desc',key: 'valueDesc' },
  { value: 'value_asc', key: 'valueAsc' },
  { value: 'ending',    key: 'ending' },
];

function highlightMatch(text, query) {
  if (!text || !query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="cl-search-results__mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function ContractList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Drafts open directly in the edit screen; everything else opens its detail.
  const contractPath = (c) => (c.status === 'draft' ? `/contracts/${c._id}/edit` : `/contracts/${c._id}`);
  const openContract = (c) => navigate(contractPath(c));
  const { items, pagination, loading } = useSelector((state) => state.contracts);
  const liveAccent = useSelector(
    (state) => state.settings?.branding?.accentColor
      || state.settings?.config?.branding?.accentColor
      || '#6366f1'
  );

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [contractType, setContractType] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companiesList, setCompaniesList] = useState([]);
  const [sortBy, setSortBy] = useState('recent');
  const [pageSize, setPageSize] = useState(() => {
    const stored = Number(window.localStorage?.getItem('cl:pageSize'));
    return [20, 50, 100].includes(stored) ? stored : 20;
  });
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === 'undefined') return 'table';
    return window.localStorage?.getItem('cl:viewMode') || 'table';
  });

  // Summary card counters. Fetched once from their own endpoint rather than
  // derived from `items`, which is only the current filtered page — deriving
  // them made every other card read 0 as soon as one was clicked.
  const [stats, setStats] = useState(null);

  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [liveHits, setLiveHits] = useState([]);
  const [liveLoading, setLiveLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchRef = useRef(null);
  const searchAbortRef = useRef(null);
  const searchBoxRef = useRef(null);

  const activeFilterCount = [status, contractType, companyFilter].filter(Boolean).length +
    (sortBy !== 'recent' ? 1 : 0);

  // Company filter options (multi-company tenants; single-company tenants
  // simply see their one company).
  useEffect(() => {
    companiesAPI.list().then(r => setCompaniesList(r.data.companies || [])).catch(() => {});
  }, []);

  // Persist view mode choice
  useEffect(() => {
    try { window.localStorage?.setItem('cl:viewMode', viewMode); } catch (e) { /* noop */ }
  }, [viewMode]);

  // ⌘K / Ctrl+K to focus search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Debounce only the search *term* so typing doesn't fire a request per
  // keystroke. Filters (status / type) stay instant — they feed the single
  // fetch effect below directly.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handle);
  }, [search]);

  // Live global search — fires in parallel with the list filter.
  useEffect(() => {
    const q = search.trim();
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    if (!q) {
      setLiveHits([]);
      setLiveLoading(false);
      return;
    }
    setLiveLoading(true);
    const controller = new AbortController();
    searchAbortRef.current = controller;
    const handle = setTimeout(async () => {
      try {
        const res = await contractsAPI.search(q, controller.signal);
        setLiveHits(res.data.hits || []);
      } catch (err) {
        if (err.name !== 'CanceledError' && err.name !== 'AbortError') {
          setLiveHits([]);
        }
      } finally {
        if (searchAbortRef.current === controller) {
          setLiveLoading(false);
          searchAbortRef.current = null;
        }
      }
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [search]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Single source of truth for the list query: runs exactly once per real
  // change to the debounced search, status, type, or page size — no double-fetch.
  useEffect(() => {
    dispatch(fetchContracts({ page: 1, limit: pageSize, search: debouncedSearch, status, contractType, companyId: companyFilter || undefined }));
  }, [dispatch, debouncedSearch, status, contractType, companyFilter, pageSize]);

  // Card totals are independent of the list query, so this runs once and its
  // values survive every filter change.
  useEffect(() => {
    let cancelled = false;
    contractsAPI.stats()
      .then(({ data }) => { if (!cancelled) setStats(data); })
      .catch(() => { /* cards fall back to '—'; the list itself still works */ });
    return () => { cancelled = true; };
  }, []);

  const handlePageChange = (page) => {
    dispatch(fetchContracts({ page, limit: pageSize, search: debouncedSearch, status, contractType, companyId: companyFilter || undefined }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    try { window.localStorage?.setItem('cl:pageSize', String(size)); } catch (e) { /* noop */ }
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatus('');
    setContractType('');
    setCompanyFilter('');
    setSortBy('recent');
  };

  const formatType = (type) => {
    if (!type) return null;
    const translated = t(`contracts.types.${type}`, { defaultValue: '' });
    if (translated && translated !== `contracts.types.${type}`) return translated;
    return type.replace(/_/g, ' ');
  };

  // Takes a contract (or a bare status value, for the column filter list) —
  // see utils/contractStatus for why 'pending_edit' has two names.
  const formatStatus = (contractOrStatus) => contractStatusLabel(contractOrStatus, t, '—');

  // Client-side sort on top of server-filtered results.
  const sortedItems = useMemo(() => {
    if (!items?.length) return items || [];
    const arr = [...items];
    const toDate = (d) => (d ? new Date(d).getTime() : 0);
    const toStr = (s) => (s || '').toLowerCase();
    switch (sortBy) {
      case 'oldest':     return arr.sort((a, b) => toDate(a.createdAt) - toDate(b.createdAt));
      case 'az':         return arr.sort((a, b) => toStr(a.contractName || a.counterpartyName).localeCompare(toStr(b.contractName || b.counterpartyName)));
      case 'za':         return arr.sort((a, b) => toStr(b.contractName || b.counterpartyName).localeCompare(toStr(a.contractName || a.counterpartyName)));
      case 'value_desc': return arr.sort((a, b) => (b.contractValue || 0) - (a.contractValue || 0));
      case 'value_asc':  return arr.sort((a, b) => (a.contractValue || 0) - (b.contractValue || 0));
      case 'ending':     return arr.sort((a, b) => toDate(a.endDate) - toDate(b.endDate));
      case 'recent':
      default:           return arr.sort((a, b) => toDate(b.createdAt) - toDate(a.createdAt));
    }
  }, [items, sortBy]);

  const daysUntil = (endDate) => {
    if (!endDate) return null;
    return differenceInDays(new Date(endDate), new Date());
  };

  const renderSkeletonRows = () => (
    Array.from({ length: 8 }).map((_, i) => (
      <tr key={i} className="cl-skeleton-row" style={{ animationDelay: `${i * 60}ms` }}>
        <td><div className="cl-skeleton cl-skeleton--id" /></td>
        <td>
          <div className="cl-skeleton-group">
            <div className="cl-skeleton cl-skeleton--icon" />
            <div>
              <div className="cl-skeleton cl-skeleton--title" />
              <div className="cl-skeleton cl-skeleton--subtitle" />
            </div>
          </div>
        </td>
        <td><div className="cl-skeleton cl-skeleton--badge" /></td>
        <td><div className="cl-skeleton cl-skeleton--text" /></td>
        <td><div className="cl-skeleton cl-skeleton--badge" /></td>
        <td><div className="cl-skeleton cl-skeleton--text-sm" /></td>
        <td><div className="cl-skeleton cl-skeleton--text" /></td>
        <td><div className="cl-skeleton cl-skeleton--avatar-text" /></td>
      </tr>
    ))
  );

  const renderSkeletonCards = () => (
    Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="cl-card cl-card--skeleton" style={{ animationDelay: `${i * 60}ms` }}>
        <div className="cl-skeleton cl-skeleton--badge" style={{ width: 70 }} />
        <div className="cl-skeleton cl-skeleton--title" style={{ width: '80%', height: 18, marginTop: 14 }} />
        <div className="cl-skeleton cl-skeleton--subtitle" style={{ width: '50%', marginTop: 8 }} />
        <div className="cl-skeleton cl-skeleton--text" style={{ width: '100%', marginTop: 22 }} />
      </div>
    ))
  );

  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;
    const { page, pages, total } = pagination;
    const limit = pagination.limit || pageSize;
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const getVisiblePages = () => {
      const visible = [];
      if (pages <= 7) {
        for (let i = 1; i <= pages; i++) visible.push(i);
      } else {
        visible.push(1);
        if (page > 3) visible.push('...');
        const rangeStart = Math.max(2, page - 1);
        const rangeEnd = Math.min(pages - 1, page + 1);
        for (let i = rangeStart; i <= rangeEnd; i++) visible.push(i);
        if (page < pages - 2) visible.push('...');
        visible.push(pages);
      }
      return visible;
    };

    return (
      <div className="cl-pagination">
        <span className="cl-pagination__info">
          {t('contracts.showingResults', { start, end, total, defaultValue: `${start}–${end} of ${total}` })}
        </span>
        <div className="cl-pagination__controls">
          <button
            className="cl-pagination__btn cl-pagination__btn--nav"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
            aria-label={t('common.previous')}
          >
            <FiChevronLeft />
          </button>
          {getVisiblePages().map((p, idx) =>
            p === '...' ? (
              <span key={`dots-${idx}`} className="cl-pagination__dots">…</span>
            ) : (
              <button
                key={p}
                className={`cl-pagination__btn ${page === p ? 'cl-pagination__btn--active' : ''}`}
                onClick={() => handlePageChange(p)}
                aria-label={`${t('common.page', { defaultValue: 'Page' })} ${p}`}
                aria-current={page === p ? 'page' : undefined}
              >
                {p}
              </button>
            )
          )}
          <button
            className="cl-pagination__btn cl-pagination__btn--nav"
            disabled={page === pages}
            onClick={() => handlePageChange(page + 1)}
            aria-label={t('common.next')}
          >
            <FiChevronRight />
          </button>
        </div>
      </div>
    );
  };

  const renderCard = (c, index) => {
    const days = daysUntil(c.endDate);
    const isExpiringSoon = days !== null && days >= 0 && days <= 30;
    const isExpired = days !== null && days < 0;
    return (
      <button
        key={c._id}
        type="button"
        className="cl-card"
        style={{ animationDelay: `${index * 40}ms` }}
        onClick={() => openContract(c)}
      >
        <div className="cl-card__top">
          <span className="cl-card__number">{c.contractNumber}</span>
          <span className={`badge badge-${c.status}`}>{formatStatus(c)}</span>
        </div>
        <div className="cl-card__body">
          <h3 className="cl-card__title">
            {c.contractName || c.counterpartyName || c.contractNumber}
            {c.versionType === 'addendum' && (
              <span className="badge badge-addendum" style={{ marginLeft: 8 }}>
                {t('contractDetail.updateTypes.addendum', { defaultValue: 'Addendum' })}
              </span>
            )}
          </h3>
          {/* Company sits ahead of the counterparty, matching the table's
              column order. */}
          {c.companyId?.name && (
            <p className="cl-card__company"><FiBriefcase /> {c.companyId.name}</p>
          )}
          {c.counterpartyName && c.contractName && (
            <p className="cl-card__counterparty">{c.counterpartyName}</p>
          )}
          {formatType(c.contractType) && (
            <span className="cl-type-tag">{formatType(c.contractType)}</span>
          )}
        </div>
        <div className="cl-card__meta">
          <div className="cl-card__meta-item">
            <FiDollarSign />
            <span>
              {c.contractValue ? `$${formatMoney(c.contractValue)}` : '—'}
            </span>
          </div>
          <div className={`cl-card__meta-item ${isExpiringSoon ? 'cl-card__meta-item--warn' : ''} ${isExpired ? 'cl-card__meta-item--danger' : ''}`}>
            <FiCalendar />
            <span>
              {formatDateMedium(c.endDate)}
            </span>
          </div>
        </div>
        <div className="cl-card__footer">
          {(() => {
            const u = c.responsibleUserId || c.internalOwner || c.createdBy;
            return u ? (
              <div className="cl-card__owner">
                <div className="cl-row__avatar">
                  {u.firstName?.[0]}{u.lastName?.[0]}
                </div>
                <span>{u.firstName} {u.lastName}</span>
              </div>
            ) : (
              <span className="cl-row__muted">{t('common.notAssigned')}</span>
            );
          })()}
          <span className="cl-card__open">
            <FiArrowRight />
          </span>
        </div>
      </button>
    );
  };

  const content = () => {
    // Skeletons on every fetch — filter/tab refetches included. Keeping the
    // old rows dimmed instead read as a frozen list until the response landed.
    const showSkeleton = loading;

    if (!loading && sortedItems.length === 0) {
      return (
        <div className="cl-empty">
          <div className="cl-empty__icon" aria-hidden="true">
            <FiFileText />
          </div>
          <h3 className="cl-empty__title">{t('contracts.noContractsFound')}</h3>
          <p className="cl-empty__text">{t('contracts.createFirstContract')}</p>
          <Link to="/contracts/new" className="cl-btn cl-btn--primary cl-btn--lg">
            <FiPlus />
            <span>{t('contracts.createContract')}</span>
          </Link>
        </div>
      );
    }

    if (viewMode === 'grid') {
      return (
        <div className="cl-grid">
          {showSkeleton ? renderSkeletonCards() : sortedItems.map((c, index) => renderCard(c, index))}
        </div>
      );
    }

    return (
      <ContractsTable
        items={sortedItems}
        loading={loading}
        pagination={pagination}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        openContract={openContract}
        contractPath={contractPath}
        formatStatus={formatStatus}
        formatType={formatType}
        renderSkeletonRows={renderSkeletonRows}
        renderPagination={renderPagination}
        // Full screen hides the page's own search box, so the table's toolbar
        // takes over the same term rather than introducing a second one.
        search={search}
        onSearchChange={setSearch}
      />
    );
  };

  return (
    <div className="cl" style={{ '--cl-accent': liveAccent }}>
      {/* Hero */}
      <header className="cl-hero">
        <PageHeader
          title={t('contracts.title')}
          description={t('pageDescriptions.contracts')}
          actions={(
            /* Creating a contract is the point of this screen, so the action
               follows the user down the list: it docks into the app header
               once it scrolls out of view and returns here on the way back
               up. Both copies stay mounted, so the hand-over is a pure CSS
               transition with nothing moving in the DOM. */
            <StickyHeaderAction accent={liveAccent}>
              <Link to="/contracts/new" className="cl-btn cl-btn--primary">
                <FiPlus />
                <span>{t('contracts.newContract')}</span>
              </Link>
            </StickyHeaderAction>
          )}
        />

        {/* Stats — counters are the user's full totals and never follow the
            list filters, so they are not tied to the list's loading state. */}
        <div className="cl-stats">
          <button
            type="button"
            className={`cl-stat ${status === '' ? 'cl-stat--active' : ''}`}
            onClick={() => setStatus('')}
          >
            <div className="cl-stat__icon cl-stat__icon--primary"><FiLayers /></div>
            <div className="cl-stat__body">
              <span className="cl-stat__label">{t('contracts.stats.total', { defaultValue: 'Total contracts' })}</span>
              <span className="cl-stat__value">{stats ? stats.total : '—'}</span>
            </div>
          </button>
          <button
            type="button"
            className={`cl-stat ${status === ACTIVE_STATUSES ? 'cl-stat--active' : ''}`}
            onClick={() => setStatus(ACTIVE_STATUSES)}
          >
            <div className="cl-stat__icon cl-stat__icon--success"><FiCheckCircle /></div>
            <div className="cl-stat__body">
              <span className="cl-stat__label">{t('contracts.stats.active', { defaultValue: 'Active' })}</span>
              <span className="cl-stat__value">{stats ? stats.active : '—'}</span>
            </div>
          </button>
          <button
            type="button"
            className={`cl-stat ${status === 'expiring_soon' ? 'cl-stat--active' : ''}`}
            onClick={() => setStatus('expiring_soon')}
          >
            <div className="cl-stat__icon cl-stat__icon--warn"><FiClock /></div>
            <div className="cl-stat__body">
              <span className="cl-stat__label">{t('contracts.stats.expiring', { defaultValue: 'Expiring soon' })}</span>
              <span className="cl-stat__value">{stats ? stats.expiring : '—'}</span>
            </div>
          </button>
          <button
            type="button"
            className={`cl-stat ${status === PENDING_STATUSES ? 'cl-stat--active' : ''}`}
            onClick={() => setStatus(PENDING_STATUSES)}
          >
            <div className="cl-stat__icon cl-stat__icon--info"><FiTrendingUp /></div>
            <div className="cl-stat__body">
              <span className="cl-stat__label">{t('contracts.stats.pending', { defaultValue: 'Pending review' })}</span>
              <span className="cl-stat__value">{stats ? stats.pending : '—'}</span>
            </div>
          </button>
        </div>
      </header>

      {/* Segmented status tabs */}
      <div className="cl-tabs-wrapper">
        <div className="cl-tabs" role="tablist">
          {STATUS_TABS.map((tab) => {
            const Icon = tab.icon;
            const active = status === tab.value;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`cl-tab ${active ? 'cl-tab--active' : ''}`}
                onClick={() => setStatus(tab.value)}
              >
                <Icon />
                <span>{t(`contracts.tabs.${tab.key}`, { defaultValue: tab.key.charAt(0).toUpperCase() + tab.key.slice(1) })}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="cl-toolbar">
        <div
          className={`cl-search ${isSearchFocused ? 'cl-search--focused' : ''}`}
          ref={searchBoxRef}
        >
          <FiSearch className="cl-search__icon" />
          <input
            ref={searchRef}
            type="text"
            className="cl-search__input"
            placeholder={t('contracts.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowResults(true); }}
            onFocus={() => { setIsSearchFocused(true); setShowResults(true); }}
            onBlur={() => setIsSearchFocused(false)}
            aria-label={t('contracts.searchPlaceholder')}
          />
          {search ? (
            <button
              className="cl-search__clear"
              onClick={() => { setSearch(''); setShowResults(false); }}
              aria-label={t('common.clearFilters')}
            >
              <FiX />
            </button>
          ) : (
            <kbd className="cl-search__kbd" aria-hidden="true">
              <FiCommand /> K
            </kbd>
          )}

          {/* Live results dropdown — grouped by hit type */}
          {showResults && search.trim() && (
            <div className="cl-search-results">
              {liveLoading && liveHits.length === 0 && (
                <div className="cl-search-results__state">
                  <span className="cl-search-results__spinner" />
                  {t('contracts.searching', { defaultValue: 'Searching everywhere…' })}
                </div>
              )}
              {!liveLoading && liveHits.length === 0 && (
                <div className="cl-search-results__state">{t('contracts.noMatches', { defaultValue: 'No matches found' })}</div>
              )}
              {liveHits.length > 0 && (() => {
                const groups = liveHits.reduce((acc, h) => {
                  (acc[h.type] = acc[h.type] || []).push(h);
                  return acc;
                }, {});
                const order = ['contract', 'field', 'document', 'document_content', 'attachment'];
                return order.filter(k => groups[k]).map((typeKey) => {
                  const meta = HIT_TYPE_META[typeKey] || { icon: FiFileText };
                  const Icon = meta.icon;
                  const hitTypeKey = typeKey === 'document_content' ? 'content' : typeKey;
                  const hitLabel = t(`contractList.hitTypes.${hitTypeKey}`, { defaultValue: typeKey });
                  return (
                    <div key={typeKey} className="cl-search-results__group">
                      <div className="cl-search-results__group-header">
                        <Icon />
                        <span>{hitLabel}</span>
                        <span className="cl-search-results__count">{groups[typeKey].length}</span>
                      </div>
                      {groups[typeKey].map((hit, i) => (
                        <button
                          key={`${typeKey}-${hit.contractId}-${hit.fieldKey || hit.documentId || i}`}
                          type="button"
                          className="cl-search-results__item"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setShowResults(false);
                            navigate(`/contracts/${hit.contractId}`);
                          }}
                        >
                          <div className="cl-search-results__item-main">
                            <span className="cl-search-results__item-title">
                              {highlightMatch(hit.label || '', search)}
                            </span>
                            <span className="cl-search-results__item-badge">{hitLabel}</span>
                            {hit.matchType === 'ocr' && (
                              <span className="cl-search-results__item-badge cl-search-results__item-badge--ocr">OCR</span>
                            )}
                            {(hit.isOldDocVersion || hit.isOldContractVersion) && (
                              <span className="cl-search-results__item-badge cl-search-results__item-badge--old">
                                {t('contractList.previousVersion', { defaultValue: 'Previous version' })}
                              </span>
                            )}
                          </div>
                          <div className="cl-search-results__item-snippet">
                            {highlightMatch(hit.snippet || '', search)}
                          </div>
                          <div className="cl-search-results__item-meta">
                            <span className="cl-search-results__item-ctr">{hit.contractNumber}</span>
                            <span className="cl-search-results__item-sep">·</span>
                            <span>{hit.contractName || hit.counterpartyName || '—'}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        <div className="cl-filters">
          <div className="cl-filter-widget">
            <SearchableSelect
              value={companyFilter}
              onChange={(v) => setCompanyFilter(v || '')}
              placeholder={t('contracts.allCompanies', { defaultValue: 'All companies' })}
              options={[
                { value: '', label: t('contracts.allCompanies', { defaultValue: 'All companies' }) },
                ...companiesList.map((c) => ({ value: c._id, label: c.name })),
              ]}
            />
          </div>

          <div className="cl-filter-widget">
            <SearchableSelect
              value={contractType}
              onChange={(v) => setContractType(v || '')}
              placeholder={t('contracts.allTypes')}
              options={[
                { value: '', label: t('contracts.allTypes') },
                ...TYPE_OPTIONS.map((type) => ({
                  value: type,
                  label: t(`contracts.types.${type}`, { defaultValue: type.replace(/_/g, ' ') }),
                })),
              ]}
            />
          </div>

          <div className="cl-filter-widget">
            <SearchableSelect
              value={sortBy}
              onChange={(v) => setSortBy(v || 'recent')}
              options={SORT_OPTIONS.map((opt) => ({
                value: opt.value,
                label: t(`contracts.sort.${opt.key}`, {
                  defaultValue: opt.key === 'recent' ? 'Most recent'
                    : opt.key === 'oldest' ? 'Oldest first'
                    : opt.key === 'az' ? 'Title A → Z'
                    : opt.key === 'za' ? 'Title Z → A'
                    : opt.key === 'valueDesc' ? 'Value high → low'
                    : opt.key === 'valueAsc' ? 'Value low → high'
                    : 'Ending soonest'
                }),
              }))}
            />
          </div>

          <div className="cl-view-toggle" role="tablist" aria-label={t('contracts.viewMode', { defaultValue: 'View' })}>
            <button
              type="button"
              className={`cl-view-toggle__btn ${viewMode === 'table' ? 'cl-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('table')}
              role="tab"
              aria-selected={viewMode === 'table'}
              aria-label={t('contracts.viewTable', { defaultValue: 'Table view' })}
            >
              <FiList />
            </button>
            <button
              type="button"
              className={`cl-view-toggle__btn ${viewMode === 'grid' ? 'cl-view-toggle__btn--active' : ''}`}
              onClick={() => setViewMode('grid')}
              role="tab"
              aria-selected={viewMode === 'grid'}
              aria-label={t('contracts.viewGrid', { defaultValue: 'Grid view' })}
            >
              <FiGrid />
            </button>
          </div>

          {activeFilterCount > 0 && (
            <button className="cl-filters__clear" onClick={clearFilters}>
              <FiX />
              <span>{t('contracts.clearFilters', { defaultValue: 'Clear' })}</span>
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {content()}

      {/* Pagination (grid view shows it here, table view renders its own at the bottom of the card) */}
      {viewMode === 'grid' && items.length > 0 && renderPagination()}
    </div>
  );
}

export default ContractList;
